import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Link, useLocation } from "@tanstack/react-router";
import { Fragment } from "react/jsx-runtime";
import { Search } from "./search";
import { ThemeSwitch } from "./theme/theme-switch";
import { NotificationBell } from "./notifications/notification-bell";
import type { NotificationAudience } from "@/types/notification";
import { cn } from "@/lib/utils";
import { DevModeBanner } from "./dev-mode-banner";
import { deriveBreadcrumbs, type Crumb } from "@/lib/breadcrumbs";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { receivesApprovals } from "@/lib/supervisor";

interface DashboardHeaderProps {
  /** A detail page's own record label ("IRA-00003", an employee's name),
   * shown after the list page it belongs to. */
  current?: string;
  /** Only for a page the sidebar doesn't list (profile, change password):
   * everywhere else the trail comes from the sidebar (deriveBreadcrumbs),
   * so it can't drift from it. */
  breadcrumbs?: Crumb[];
}

export function DashboardHeader({ current, breadcrumbs: fallback }: DashboardHeaderProps) {
  // Scope the bell to the area the user is currently in (not their role), so a
  // supervisor sees personal items under /user and approval items under
  // /supervisor.
  const pathname = useLocation({ select: (l) => l.pathname });
  const breadcrumbs = deriveBreadcrumbs(pathname, current) ?? fallback ?? [];
  const area: { audience: NotificationAudience; notificationsPath: string } =
    pathname.startsWith("/admin")
      ? { audience: "ADMIN", notificationsPath: "/admin/notifications" }
      : pathname.startsWith("/supervisor")
        ? { audience: "SUPERVISOR", notificationsPath: "/supervisor/notifications" }
        : { audience: "USER", notificationsPath: "/user/notifications" };
  // Approval notifications are written with audience SUPERVISOR, so a bell
  // scoped to ADMIN or USER never badged them for an approver working outside
  // /supervisor (T-165). Approvers get the unscoped bell everywhere else.
  const { data: capabilities } = useMyPermissions();
  const audience =
    area.audience !== "SUPERVISOR" && receivesApprovals(capabilities)
      ? undefined
      : area.audience;
  return (
    <header
      className={cn(
        "z-50 sticky top-0 px-1 mb-3 bg-background flex h-14 shrink-0 items-center justify-between gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12"
        // No dev-banner offset needed: in dev builds the page scrolls inside
        // #root, which already starts below the banner (html.dev-mode).
      )}
    >
<DevModeBanner />
      <div className="flex items-center gap-2 px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mr-2 data-[orientation=vertical]:h-4"
        />
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return (
                <Fragment key={index}>
                  <BreadcrumbItem
                    className={index === 0 ? "hidden md:block" : ""}
                  >
                    {/* Only the page you're on stands out; everything
                        above it stays muted, links included (they darken
                        on hover). A group (Stock, Attendance) isn't a
                        page, so it's plain text, not a link. */}
                    {isLast ? (
                      <BreadcrumbPage className="font-medium">
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : crumb.href ? (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.href}>{crumb.label}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <span>{crumb.label}</span>
                    )}
                  </BreadcrumbItem>
                  {!isLast && (
                    <BreadcrumbSeparator className="hidden md:block" />
                  )}
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div className="pr-4 flex items-center gap-x-4">
        <div className="lg:block hidden">
          <Search />
        </div>
        <ThemeSwitch />
        <NotificationBell
          viewAllPath={area.notificationsPath}
          audience={audience}
        />
        {/* <ProfileDropdown /> */}
      </div>
    </header>
  );
}
